import { z } from 'zod';

import {
  ContentType,
  MessageStopReason,
  RunErrorCode as RunErrorCodeEnum,
  ToolErrorCode,
  ToolErrorKind,
} from '../enums';

/**
 * Kanbu agent stream protocol, version 3.
 *
 * Content-block vocabulary shape-identical to `@langchain/protocol`
 * (same field names and discriminators). Types come from `z.infer` —
 * do **not** import block types from `@langchain/protocol` (its
 * `TextContentBlock.type` is a plain `string`, which de-discriminates
 * the union). Runtime code uses `ContentBlock.*` from
 * `@langchain/core/messages`; the wire validates against this schema.
 *
 * Framing stays Kanbu NDJSON. The envelope (`method` / channel names)
 * may still be typed from `@langchain/protocol`; blocks and Kanbu
 * transport events live here.
 */
export const KANBU_PROTOCOL_VERSION = 3 as const;

export const stopReasonSchema = z.nativeEnum(MessageStopReason);

export type StopReason = z.infer<typeof stopReasonSchema>;

export const toolErrorKindSchema = z.nativeEnum(ToolErrorKind);
export const toolErrorCodeSchema = z.nativeEnum(ToolErrorCode);

export const toolErrorArtifactSchema = z.object({
  type: z.literal('tool_error'),
  code: toolErrorCodeSchema,
  kind: toolErrorKindSchema,
  retryable: z.boolean(),
});

export type ToolErrorArtifact = z.infer<typeof toolErrorArtifactSchema>;

export const evidenceSnapshotSchema = z.object({
  text: z.string().max(24000),
  hash: z.string(),
  capturedAt: z.string(),
  truncated: z.boolean(),
});

export const answerAttributionSchema = z.object({
  status: z.enum(['selected', 'none', 'missing', 'interrupted', 'legacy']),
  method: z.enum(['inline', 'tool']),
  answerId: z.string().optional(),
  sourceIds: z.array(z.string()),
});
export type AnswerAttribution = z.infer<typeof answerAttributionSchema>;

export const registeredCitationSourceSchema = z.object({
  type: z.nativeEnum(ContentType),
  fileName: z.string(),
  url: z.string(),
  page: z.string(),
  mimeType: z
    .string()
    .nullish()
    .transform(value => value ?? undefined),
  documentId: z.string().optional(),
  sourceKey: z.string().optional(),
  header: z.string().optional(),
  article: z.string().optional(),
  evidence: evidenceSnapshotSchema.optional(),
  /** Compatibility with artifacts persisted before evidence snapshots. */
  chunkText: z.string().optional(),
});

export const citationSourcesArtifactSchema = z.object({
  type: z.literal('citation_sources'),
  sources: z.record(registeredCitationSourceSchema),
});

export type RegisteredCitationSource = z.infer<
  typeof registeredCitationSourceSchema
>;
export type CitationSourcesArtifact = z.infer<
  typeof citationSourcesArtifactSchema
>;

/**
 * Stable, machine-readable run failure codes. Eval harness and ops
 * dashboards depend on these values — keep the union closed.
 */
export const runErrorCodeSchema = z.nativeEnum(RunErrorCodeEnum);

export type RunErrorCode = z.infer<typeof runErrorCodeSchema>;

export const streamMessageStatusSchema = z.enum([
  'start',
  'streaming',
  'end',
  'error',
  'interrupted',
]);

export type StreamMessageStatus = z.infer<typeof streamMessageStatusSchema>;

export const messageRoleSchema = z.enum(['ai', 'human', 'system']);

export type MessageRole = z.infer<typeof messageRoleSchema>;

const blockIndexSchema = z.union([z.number(), z.string()]);

export const citationAnnotationSchema = z.object({
  type: z.literal('citation'),
  scope: z.enum(['document', 'passage']).optional(),
  id: z.string().optional(),
  url: z.string().optional(),
  title: z.string().optional(),
  /**
   * Document type of the cited source (a `ContentType`). Kept as a plain
   * string so the wire contract stays free of the entity enum. Without it
   * a client deriving sources from annotations alone has to guess, and
   * guessing "website" mislabels every uploaded document.
   */
  source_type: z.string().optional(),
  start_index: z.number().optional(),
  end_index: z.number().optional(),
  cited_text: z.string().optional(),
});

export type CitationAnnotation = z.infer<typeof citationAnnotationSchema>;

export const nonStandardAnnotationSchema = z.object({
  type: z.literal('non_standard_annotation'),
  id: z.string().optional(),
  value: z.record(z.unknown()),
});

export const annotationSchema = z.discriminatedUnion('type', [
  citationAnnotationSchema,
  nonStandardAnnotationSchema,
]);

export type Annotation = z.infer<typeof annotationSchema>;

export const textContentBlockSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
  id: z.string().optional(),
  index: blockIndexSchema.optional(),
  annotations: z.array(annotationSchema).optional(),
  attribution: answerAttributionSchema.optional(),
});

export type TextContentBlock = z.infer<typeof textContentBlockSchema>;

export const reasoningContentBlockSchema = z.object({
  type: z.literal('reasoning'),
  reasoning: z.string().optional(),
  id: z.string().optional(),
  index: blockIndexSchema.optional(),
});

export type ReasoningContentBlock = z.infer<typeof reasoningContentBlockSchema>;

export const toolCallBlockSchema = z.object({
  type: z.literal('tool_call'),
  id: z.string().nullable(),
  name: z.string(),
  args: z.record(z.unknown()),
  index: blockIndexSchema.optional(),
});

export type ToolCallBlock = z.infer<typeof toolCallBlockSchema>;

export const toolCallChunkBlockSchema = z.object({
  type: z.literal('tool_call_chunk'),
  id: z.string().nullable(),
  name: z.string().nullable(),
  args: z.string().nullable(),
  index: blockIndexSchema.optional(),
});

export type ToolCallChunkBlock = z.infer<typeof toolCallChunkBlockSchema>;

export const invalidToolCallBlockSchema = z.object({
  type: z.literal('invalid_tool_call'),
  id: z.string().nullable(),
  name: z.string().nullable(),
  args: z.string().nullable(),
  error: z.string().nullable(),
  index: blockIndexSchema.optional(),
});

export const serverToolCallBlockSchema = z.object({
  type: z.literal('server_tool_call'),
  id: z.string(),
  name: z.string(),
  args: z.record(z.unknown()),
  index: blockIndexSchema.optional(),
});

export const serverToolResultBlockSchema = z.object({
  type: z.literal('server_tool_result'),
  tool_call_id: z.string(),
  status: z.enum(['success', 'error']),
  id: z.string().optional(),
  output: z.unknown().optional(),
  index: blockIndexSchema.optional(),
});

/**
 * Tool result as a message content block (assistant turn projection).
 * Distinct from tools-channel `tool-finished` events.
 */
export const toolDisplaySchema = z.enum(['card', 'activity', 'hidden']);

export type ToolDisplay = z.infer<typeof toolDisplaySchema>;

export const toolResultBlockSchema = z.object({
  type: z.literal('tool_result'),
  tool_call_id: z.string(),
  name: z.string().optional(),
  content: z.string(),
  artifact: z.unknown().optional(),
  is_error: z.boolean().optional(),
  id: z.string().optional(),
  index: blockIndexSchema.optional(),
  /** Server-declared presentation, stamped at tool-started. */
  display: toolDisplaySchema.optional(),
  kind: z.string().optional(),
});

export type ToolResultBlock = z.infer<typeof toolResultBlockSchema>;

export const imageContentBlockSchema = z.object({
  type: z.literal('image'),
  id: z.string().optional(),
  file_id: z.string().optional(),
  url: z.string().optional(),
  base64: z.string().optional(),
  mime_type: z.string().optional(),
  index: blockIndexSchema.optional(),
});

export const audioContentBlockSchema = z.object({
  type: z.literal('audio'),
  id: z.string().optional(),
  file_id: z.string().optional(),
  url: z.string().optional(),
  base64: z.string().optional(),
  mime_type: z.string().optional(),
  index: blockIndexSchema.optional(),
});

export const fileContentBlockSchema = z.object({
  type: z.literal('file'),
  id: z.string().optional(),
  file_id: z.string().optional(),
  url: z.string().optional(),
  base64: z.string().optional(),
  mime_type: z.string().optional(),
  index: blockIndexSchema.optional(),
});

/**
 * Kanbu product extensions ride in `non_standard` with a discriminant
 * inside `value`. Narrow once at the render boundary.
 */
export const productCardsValueSchema = z.object({
  kind: z.literal('product_cards'),
  toolCallId: z.string(),
  name: z.string(),
  args: z.record(z.unknown()),
  artifact: z.unknown().optional(),
});

export const operatorValueSchema = z.object({
  kind: z.literal('operator'),
  toolCallId: z.string(),
  name: z.string(),
  args: z.record(z.unknown()),
  artifact: z.unknown().optional(),
});

export const formValueSchema = z.object({
  kind: z.literal('form'),
  toolCallId: z.string(),
  name: z.string(),
  artifact: z.unknown(),
});

export type FormBlockValue = z.infer<typeof formValueSchema>;

export const kanbuNonStandardValueSchema = z.discriminatedUnion('kind', [
  productCardsValueSchema,
  operatorValueSchema,
  formValueSchema,
]);

export type KanbuNonStandardValue = z.infer<typeof kanbuNonStandardValueSchema>;

export const nonStandardContentBlockSchema = z.object({
  type: z.literal('non_standard'),
  value: z.record(z.unknown()),
  id: z.string().optional(),
  index: blockIndexSchema.optional(),
});

export type NonStandardContentBlock = z.infer<
  typeof nonStandardContentBlockSchema
>;

export const contentBlockSchema = z.discriminatedUnion('type', [
  textContentBlockSchema,
  reasoningContentBlockSchema,
  toolCallBlockSchema,
  toolCallChunkBlockSchema,
  invalidToolCallBlockSchema,
  serverToolCallBlockSchema,
  serverToolResultBlockSchema,
  toolResultBlockSchema,
  imageContentBlockSchema,
  audioContentBlockSchema,
  fileContentBlockSchema,
  nonStandardContentBlockSchema,
]);

export type ContentBlock = z.infer<typeof contentBlockSchema>;

export const finalizedContentBlockSchema = z.discriminatedUnion('type', [
  textContentBlockSchema,
  reasoningContentBlockSchema,
  toolCallBlockSchema,
  invalidToolCallBlockSchema,
  serverToolCallBlockSchema,
  serverToolResultBlockSchema,
  toolResultBlockSchema,
  imageContentBlockSchema,
  audioContentBlockSchema,
  fileContentBlockSchema,
  nonStandardContentBlockSchema,
]);

export type FinalizedContentBlock = z.infer<typeof finalizedContentBlockSchema>;

export const textDeltaSchema = z.object({
  type: z.literal('text-delta'),
  text: z.string(),
});

export const reasoningDeltaSchema = z.object({
  type: z.literal('reasoning-delta'),
  reasoning: z.string(),
});

export const dataDeltaSchema = z.object({
  type: z.literal('data-delta'),
  data: z.string(),
  encoding: z.literal('base64').optional(),
});

export const blockDeltaSchema = z.object({
  type: z.literal('block-delta'),
  fields: z
    .object({
      type: z.string(),
    })
    .passthrough(),
});

export const contentBlockDeltaSchema = z.discriminatedUnion('type', [
  textDeltaSchema,
  reasoningDeltaSchema,
  dataDeltaSchema,
  blockDeltaSchema,
]);

export type ContentBlockDelta = z.infer<typeof contentBlockDeltaSchema>;

export const usageInfoSchema = z.object({
  input_tokens: z.number().optional(),
  output_tokens: z.number().optional(),
  total_tokens: z.number().optional(),
});

export type UsageInfo = z.infer<typeof usageInfoSchema>;

export const messageMetadataSchema = z
  .object({
    provider: z.string().optional(),
    model: z.string().optional(),
    model_type: z.string().optional(),
    run_id: z.string().optional(),
    thread_id: z.string().optional(),
  })
  .passthrough();

export type MessageMetadata = z.infer<typeof messageMetadataSchema>;

/** Persisted / assembled turn message (blocks when structured). */
export const turnMessageSchema = z.object({
  id: z.string(),
  role: messageRoleSchema,
  blocks: z.array(contentBlockSchema),
  usage: usageInfoSchema.optional(),
  metadata: messageMetadataSchema.optional(),
});

export type TurnMessage = z.infer<typeof turnMessageSchema>;

export const turnSchema = z.object({
  messages: z.array(turnMessageSchema),
});

export type Turn = z.infer<typeof turnSchema>;

// ---------------------------------------------------------------------------
// Wire events (Kanbu NDJSON lines)
// ---------------------------------------------------------------------------

export const runStartEventSchema = z.object({
  type: z.literal('run.start'),
  /**
   * R-85: a plain `number`, not `z.literal(KANBU_PROTOCOL_VERSION)`. A
   * mismatched version is an expected, recoverable case — a stale cached
   * client bundle talking to a server that bumped the protocol — and the
   * client-side stale-bundle reload guard (`isStaleProtocol` in
   * `apps/chatbot-app/src/lib/streamingUtils.ts`) needs the event to decode
   * successfully so it can inspect the field and react. A literal here
   * makes decoding itself the enforcement point, throwing a generic
   * `ProtocolDecodeError` before the event — and the version number it
   * carries — ever reaches that guard.
   */
  protocol: z.number(),
  runId: z.string(),
  threadId: z.string(),
  messageId: z.string(),
});

export type RunStartEvent = z.infer<typeof runStartEventSchema>;

export const messageStartEventSchema = z.object({
  type: z.literal('message-start'),
  messageId: z.string(),
  role: messageRoleSchema,
  id: z.string(),
  metadata: messageMetadataSchema.optional(),
});

export type MessageStartEvent = z.infer<typeof messageStartEventSchema>;

export const contentBlockStartEventSchema = z.object({
  type: z.literal('content-block-start'),
  messageId: z.string(),
  index: z.number(),
  content: contentBlockSchema,
});

export type ContentBlockStartEvent = z.infer<
  typeof contentBlockStartEventSchema
>;

export const contentBlockDeltaEventSchema = z.object({
  type: z.literal('content-block-delta'),
  messageId: z.string(),
  index: z.number(),
  delta: contentBlockDeltaSchema,
});

export type ContentBlockDeltaEvent = z.infer<
  typeof contentBlockDeltaEventSchema
>;

export const contentBlockFinishEventSchema = z.object({
  type: z.literal('content-block-finish'),
  messageId: z.string(),
  index: z.number(),
  content: finalizedContentBlockSchema,
});

export type ContentBlockFinishEvent = z.infer<
  typeof contentBlockFinishEventSchema
>;

export const messageFinishEventSchema = z.object({
  type: z.literal('message-finish'),
  messageId: z.string(),
  usage: usageInfoSchema.optional(),
  stopReason: stopReasonSchema.optional(),
});

export type MessageFinishEvent = z.infer<typeof messageFinishEventSchema>;

/**
 * Follow-up question suggestions for the turn that just finished, from a
 * post-turn cheap-model call — a first-class event rather than a field on
 * `message-finish` so the (bounded-wait) generation never blocks or
 * reshapes that event. Absent/empty means none fit; the client keeps
 * whatever it already had.
 */
export const followUpsEventSchema = z.object({
  type: z.literal('follow-ups'),
  messageId: z.string(),
  questions: z.array(z.string()),
});

export type FollowUpsEvent = z.infer<typeof followUpsEventSchema>;

export const toolStartedEventSchema = z.object({
  type: z.literal('tool-started'),
  messageId: z.string(),
  tool_call_id: z.string(),
  tool_name: z.string(),
  input: z.unknown().optional(),
  /** Model-authored activity label when tool activity display is on. */
  label: z.string().optional(),
  /**
   * Server-declared presentation. Replaces client-side CARD_TOOL_NAMES /
   * form sniffing / load_skill prefix detection.
   */
  display: z.enum(['card', 'activity', 'hidden']).optional(),
  kind: z.string().optional(),
});

export type ToolStartedEvent = z.infer<typeof toolStartedEventSchema>;

export const toolFinishedEventSchema = z.object({
  type: z.literal('tool-finished'),
  messageId: z.string(),
  tool_call_id: z.string(),
  output: z.unknown(),
  is_error: z.boolean().optional(),
});

export type ToolFinishedEvent = z.infer<typeof toolFinishedEventSchema>;

export const toolErrorEventSchema = z.object({
  type: z.literal('tool-error'),
  messageId: z.string(),
  tool_call_id: z.string(),
  message: z.string(),
  code: toolErrorCodeSchema,
});

export type ToolErrorEvent = z.infer<typeof toolErrorEventSchema>;

/**
 * Form interrupt — run pauses for user input. Carried as a dedicated
 * event rather than only a non_standard block so the client can end the
 * stream cleanly before the form is answered.
 */
export const toolAwaitingUserEventSchema = z.object({
  type: z.literal('tool.awaiting_user'),
  messageId: z.string(),
  toolCallId: z.string(),
  name: z.string(),
  artifact: z.unknown(),
});

export type ToolAwaitingUserEvent = z.infer<typeof toolAwaitingUserEventSchema>;

export const runEndEventSchema = z.object({
  type: z.literal('run.end'),
  runId: z.string(),
  stopReason: stopReasonSchema.optional(),
});

export type RunEndEvent = z.infer<typeof runEndEventSchema>;

export const runErrorEventSchema = z.object({
  type: z.literal('run.error'),
  runId: z.string(),
  code: runErrorCodeSchema,
  message: z.string(),
});

export type RunErrorEvent = z.infer<typeof runErrorEventSchema>;

export const agentProtocolEventSchema = z.discriminatedUnion('type', [
  runStartEventSchema,
  messageStartEventSchema,
  contentBlockStartEventSchema,
  contentBlockDeltaEventSchema,
  contentBlockFinishEventSchema,
  messageFinishEventSchema,
  followUpsEventSchema,
  toolStartedEventSchema,
  toolFinishedEventSchema,
  toolErrorEventSchema,
  toolAwaitingUserEventSchema,
  runEndEventSchema,
  runErrorEventSchema,
]);

export type AgentProtocolEvent = z.infer<typeof agentProtocolEventSchema>;

export type AgentProtocolEventType = AgentProtocolEvent['type'];

export const AGENT_PROTOCOL_EVENT_TYPES: ReadonlySet<AgentProtocolEventType> =
  new Set(
    agentProtocolEventSchema.options.map(
      option => option.shape.type.value as AgentProtocolEventType,
    ),
  );
